import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { DatePicker, Modal } from 'antd';
import { Button } from 'components/Button';
import { downloadAttendanceBySchedule } from 'actions/profileNew';
import { FiMoreVertical } from 'react-icons/fi';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import { storeEndDate, storeSelectedRange, storeStartDate, usersDownloadReport } from 'actions/attendanceNew';
import { setSpecificUserId } from 'actions/userManagementV2';
import ReportDrawer from 'pages/userManagementV3/localComponent/reportDrawer';
import MarkAttendanceDrawer from 'pages/userManagementV3/localComponent/markAttendanceDrawer';
import SettingsDrawer from 'pages/userManagementV3/localComponent/settingsDrawer';

// "⋮" actions beside the staff profile "Attendance summary" title — opens the
// same drawers as the V3 profile attendance tab (userAttendanceTabV3.js).
// Menu look matches StaffListTable's RowActionsMenu.

// Time sheet = the user's clock in / clock out; schedule = the teacher's
// scheduled classes (teachers only).
// Students: only the schedule report (their class attendance).
const getMenuItems = (isTeacher, isStudent) => (isStudent ? [
    { key: 'downloadSchedule', label: 'Download report by schedule' },
] : [
    { key: 'webClock', label: 'Web clock in/clock out' },
    { key: 'markAttendance', label: 'Mark attendance' },
    { key: 'downloadReport', label: 'Download report by time sheet' },
    ...(isTeacher ? [{ key: 'downloadSchedule', label: 'Download report by schedule' }] : []),
]);

const AttendanceSummaryActions = ({
    userId, isTeacher, isStudent = false, markAttendanceRequest, onAttendanceMarked, startDateContainer, endDateContainer, selectedRangeContainer,
    storeStartDate, storeEndDate, storeSelectedRange, usersDownloadReport, setSpecificUserId, downloadAttendanceBySchedule,
}) => {
    const MENU_ITEMS = getMenuItems(isTeacher, isStudent);
    // "Download report by schedule" — month picker modal.
    const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
    const [scheduleMonth, setScheduleMonth] = useState(() => moment());
    const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
    const [markDrawerVisible, setMarkDrawerVisible] = useState(false);
    const [reportDrawerVisible, setReportDrawerVisible] = useState(false);
    const [attendanceFormDetails, setAttendanceFormDetails] = useState({
        date: null,
        clockIn: null,
        clockOut: null,
        totalHours: null,
        reason: null,
    });
    const [isChecked, setIsChecked] = useState(false);
    const [isChecked1, setIsChecked1] = useState(false);
    const [durationSettings, setDurationSettings] = useState({ duration: 'Permanent', startDate: '', endDate: '' });
    const [open, setOpen] = useState(false);
    const [hoveredKey, setHoveredKey] = useState(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // "Mark attendance" from a calendar tooltip — open the drawer with that date.
    useEffect(() => {
        if (!markAttendanceRequest?.date) return;
        setAttendanceFormDetails({
            date: moment(markAttendanceRequest.date, 'YYYY-MM-DD'),
            clockIn: null,
            clockOut: null,
            totalHours: null,
            reason: null,
        });
        setMarkDrawerVisible(true);
    }, [markAttendanceRequest]);

    const handleMenuClick = ({ key }) => {
        setOpen(false);
        setHoveredKey(null);
        if (key === 'webClock') setSettingsDrawerVisible(true);
        else if (key === 'markAttendance') setMarkDrawerVisible(true);
        else if (key === 'downloadReport') {
            // usersDownloadReport reads the user id from userManagementV2.specificUserId.
            setSpecificUserId(userId);
            setReportDrawerVisible(true);
        } else if (key === 'downloadSchedule') {
            setScheduleMonth(moment());
            setScheduleModalVisible(true);
        }
    };

    const handleScheduleDownload = () => {
        if (!scheduleMonth) return;
        downloadAttendanceBySchedule({
            userId,
            attendanceDetailsMonth: scheduleMonth.format('MMMM'),
            attendanceByScheduleYear: scheduleMonth.format('YYYY'),
        });
        setScheduleModalVisible(false);
    };

    return (
        <>
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                    type="button"
                    aria-label="Attendance actions"
                    onClick={() => setOpen((o) => !o)}
                    style={{
                        width: fluid(44),
                        height: fluid(44),
                        border: 'none',
                        borderRadius: fluid(8),
                        backgroundColor: open ? '#F1F1F1' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <FiMoreVertical size={22} color="#666666" strokeWidth={2} />
                </button>

                {open && (
                    <div style={{
                        position: 'absolute',
                        top: fluid(48),
                        right: 0,
                        minWidth: fluid(140),
                        backgroundColor: '#FFFFFF',
                        borderRadius: fluid(16),
                        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                        padding: fluid(8),
                        zIndex: 30,
                    }}>
                        {MENU_ITEMS.map((item) => (
                            <div
                                key={item.key}
                                onClick={() => handleMenuClick({ key: item.key })}
                                onMouseEnter={() => setHoveredKey(item.key)}
                                onMouseLeave={() => setHoveredKey((k) => (k === item.key ? null : k))}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: `${fluid(10)} ${fluid(12)}`,
                                    borderRadius: 0,
                                    fontSize: fluid(16),
                                    fontWeight: 500,
                                    color: '#191919',
                                    backgroundColor: hoveredKey === item.key ? '#F1F1F1' : 'transparent',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <SettingsDrawer
                settingsDrawerVisible={settingsDrawerVisible}
                setSettingsDrawerVisible={setSettingsDrawerVisible}
                userId={userId}
                isChecked={isChecked}
                isChecked1={isChecked1}
                setIsChecked={setIsChecked}
                setIsChecked1={setIsChecked1}
                durationSettings={durationSettings}
                setDurationSettings={setDurationSettings}
            />
            <MarkAttendanceDrawer
                markDrawerVisible={markDrawerVisible}
                setMarkDrawerVisible={setMarkDrawerVisible}
                setAttendanceFormDetails={setAttendanceFormDetails}
                attendanceFormDetails={attendanceFormDetails}
                userId={userId}
                onSaved={onAttendanceMarked}
            />
            <Modal
                open={scheduleModalVisible}
                onCancel={() => setScheduleModalVisible(false)}
                footer={null}
                centered
                className="modal-round-corner"
                destroyOnClose
            >
                <div style={{ padding: fluid(8), display: 'flex', flexDirection: 'column', gap: fluid(20) }}>
                    <div style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(20), fontWeight: 500, color: '#191919', textAlign: 'center' }}>
                        Download report by schedule
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(8) }}>
                        <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(14), color: '#636363' }}>Select month</span>
                        <DatePicker
                            picker="month"
                            format="MMMM YYYY"
                            allowClear={false}
                            value={scheduleMonth}
                            onChange={(val) => setScheduleMonth(val)}
                            disabledDate={(current) => current && current.isAfter(moment(), 'month')}
                            style={{ width: '100%', height: 42, borderRadius: 12 }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button type="primary" disabled={!scheduleMonth} onClick={handleScheduleDownload} style={{ minWidth: 140 }}>
                            DOWNLOAD
                        </Button>
                    </div>
                </div>
            </Modal>

            <ReportDrawer
                drawerVisible={reportDrawerVisible}
                setDrawerVisible={setReportDrawerVisible}
                startDateContainer={startDateContainer}
                endDateContainer={endDateContainer}
                selectedRangeContainer={selectedRangeContainer}
                storeStartDate={storeStartDate}
                storeEndDate={storeEndDate}
                storeSelectedRange={storeSelectedRange}
                usersDownloadReport={usersDownloadReport}
            />
        </>
    );
};

const mapStateToProps = (state) => {
    const { startDateContainer, selectedRangeContainer, endDateContainer } = state.attendanceNew;
    return { startDateContainer, selectedRangeContainer, endDateContainer };
};

const mapDispatchToProps = (dispatch) => ({
    storeStartDate: (val) => dispatch(storeStartDate(val)),
    storeEndDate: (val) => dispatch(storeEndDate(val)),
    storeSelectedRange: (val) => dispatch(storeSelectedRange(val)),
    usersDownloadReport: (callback, successCallBack) => dispatch(usersDownloadReport(callback, successCallBack)),
    setSpecificUserId: (id) => dispatch(setSpecificUserId(id)),
    downloadAttendanceBySchedule: (params) => dispatch(downloadAttendanceBySchedule(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AttendanceSummaryActions);
